/**
 * Step configuration view for an action: app picker, field chips + Override + Advanced mode for full ActionPanel.
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector, useDispatch, useStore, Provider } from 'react-redux';
import { Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip, Divider, Avatar, Tooltip, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import axios from 'axios';
import { selectElementById, selectWorkflowElements, elementUpdated, saveWorkflow, selectLoadingPerform, loadingperform } from '../../features/workflow/workflowSlice';
import ActionPanel from './ActionPanel';
import AppPickerModal from './AppPickerModal';
import EventPickerModal from './EventPickerModal';
import { ReactFlowProvider } from 'react-flow-renderer';
import { SingleFieldEditor, FieldsRender } from '../helpers/panel-functions';
import { stepRequiresAuth, hasAuth, getStepConfigStatus } from '../helpers/stepReady';
import Authorizer from '../Authorizer';
import { saveRecentApp } from '../../utils/recentApps';
import {
  getOverrideRowField,
  getUnmetDynamicParams,
  getNestedValue,
  setNestedValue,
  getSchemaAtPath,
  mergeFieldsPreferRicherBody,
} from '../../utils/dynamicFieldDeps';
import { canRefreshPerformFields, refreshPerformFields } from '../../utils/performFields';
import RefreshRoundedIcon from '@material-ui/icons/RefreshRounded';
import StepTestSection, {
  StepProgressPills,
} from './StepTestSection';
import { getDefaultWizardStep, getWizardStepState } from '../helpers/stepReady';
import styles from '../Panel.module.css';


const HEADER_BLUE = '#0069ea';

const useStyles = makeStyles((theme) => ({
  panelWrapper: {
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: theme.shadows[4],
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  panelWrapperInline: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    marginTop: 0,
  },
  headerBar: {
    padding: theme.spacing(2),
    backgroundColor: HEADER_BLUE,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  headerLogoCircle: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    '& img': {
      width: 22,
      height: 22,
      objectFit: 'contain',
    },
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '1.1rem',
    color: '#fff',
  },
  contentArea: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(4),
    maxWidth: 480,
    backgroundColor: theme.palette.background.paper,
    minHeight: 200,
  },
  contentAreaInline: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  subTitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1.5),
  },
  divider: {
    marginBottom: theme.spacing(2),
  },
  sectionLabel: {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  appIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    objectFit: 'contain',
    backgroundColor: theme.palette.action.hover,
  },
  appIconButton: {
    padding: 0,
    borderRadius: 8,
    cursor: 'pointer',
    border: `1px solid transparent`,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
  },
  appIconPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '1rem',
  },
  actionName: {
    fontWeight: 600,
    flex: 1,
  },
  eventRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1, 1.25),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  chipsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.5),
  },
  chip: {
    fontSize: '0.75rem',
  },
  keyFieldLine: {
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.5),
  },
  statusReady: {
    color: theme.palette.success.main,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  statusNeedsInput: {
    color: theme.palette.warning.dark || theme.palette.warning.main,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  statusBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.25),
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    fontSize: '0.875rem',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s ease, background-color 0.15s ease',
    '&:hover': {
      boxShadow: `0 0 0 1px ${HEADER_BLUE}`,
    },
  },
  statusBannerReady: {
    backgroundColor: 'rgba(46, 125, 50, 0.08)',
    border: '1px solid rgba(46, 125, 50, 0.25)',
  },
  statusBannerNeeds: {
    backgroundColor: 'rgba(245, 124, 0, 0.08)',
    border: '1px solid rgba(245, 124, 0, 0.3)',
  },
  statusBannerButton: {
    textTransform: 'none',
    justifyContent: 'flex-start',
    padding: 0,
    minWidth: 0,
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: 1.4,
    textAlign: 'left',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  footerContinue: {
    marginTop: theme.spacing(2),
    width: '100%',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 15,
    padding: '10px 16px',
    borderRadius: 8,
  },
  buttonRow: {
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  overrideBanner: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: 'rgba(255, 193, 7, 0.15)',
    border: '1px solid rgba(255, 152, 0, 0.5)',
  },
  overrideBannerText: {
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  overrideScroll: {
    flex: '1 1 auto',
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
    paddingBottom: theme.spacing(2),
    maxHeight: 'calc(100vh - 420px)',
  },
}));

/** True if field is body/formData with nested object value (show key + value per row instead of [object Object]). */
function isBodyFormDataField(f) {
  return (f?.in === 'formData' || f?.name === 'body') && f?.value != null && typeof f.value === 'object' && !Array.isArray(f.value);
}

/** True if value is empty (should be scrubbed from body or not shown as "mapped"). */
function isValueEmpty(v) {
  if (v == null) return true;
  if (v === '') return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v).length === 0;
  return false;
}

/** Human-readable key name from schema or key. */
function keyTitle(schema, key) {
  if (schema?.title) return schema.title;
  if (!key || typeof key !== 'string') return String(key ?? '');
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
}

/** Format any value as "Key Name: Value" string for read-only display. Handles objects (recursive), arrays, primitives. */
function formatValueForKeyValueDisplay(value, schema) {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    if (value.startsWith('${') || value.includes('outputFields.')) return 'From previous step';
    return value;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    const itemSchema = schema?.items;
    const valid = value.filter((x) => x != null && x !== '');
    if (valid.length === 0) return '—';
    const allPrimitive = valid.every((x) => typeof x !== 'object' || x === null);
    if (allPrimitive) return valid.map((x) => String(x)).join(', ');
    return valid.map((item) => formatValueForKeyValueDisplay(item, itemSchema)).filter(Boolean).join('\n');
  }
  if (typeof value === 'object') {
    if (value.label != null || value.value != null) return value.label ?? value.value ?? '—';
    const lines = [];
    for (const key of Object.keys(value)) {
      const propSchema = schema?.properties?.[key];
      const title = keyTitle(propSchema, key);
      const sub = value[key];
      const subStr = formatValueForKeyValueDisplay(sub, propSchema);
      if (subStr == null || subStr === '') continue;
      if (subStr.includes('\n')) lines.push(title + ':\n  ' + subStr.replace(/\n/g, '\n  '));
      else lines.push(title + ': ' + subStr);
    }
    return lines.join('\n') || '—';
  }
  return String(value);
}

/** Single display string for a value (label, value, array as comma-separated, or "From previous step" for ${...} refs). */
function formatSingleValue(v, schema) {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) {
    const valid = v.filter((item) => item != null && item !== '');
    if (valid.length === 0) return '—';
    const hasObjects = valid.some((item) => typeof item === 'object' && item && !(item.label != null || item.value != null));
    if (hasObjects) return formatValueForKeyValueDisplay(v, schema);
    return valid.map((item) => (typeof item === 'object' && item && (item.label != null || item.value != null) ? (item.label ?? item.value ?? '') : String(item))).filter(Boolean).join(', ') || '—';
  }
  if (typeof v === 'object') {
    if (v.label != null || v.value != null) return v.label ?? v.value ?? '—';
    return formatValueForKeyValueDisplay(v, schema);
  }
  if (typeof v === 'string' && (v.startsWith('${') || v.includes('outputFields.'))) return 'From previous step';
  return String(v);
}

/** Display label for a simple field (path/params). Returns null for body/formData so caller expands. */
function formatFieldDisplayLabel(f) {
  const v = f?.value;
  if (v == null) return '';
  if (isBodyFormDataField(f)) return null;
  if (typeof v === 'object' && !Array.isArray(v) && (v.label != null || v.value != null)) return v.label ?? v.value ?? '';
  if (typeof v === 'object' || Array.isArray(v)) return formatValueForKeyValueDisplay(v, f?.schema);
  return String(v);
}

/** Parse "${nodes.UUID.outputFields.name}" -> { nodeId, fieldKey }. */
function parseRef(ref) {
  if (typeof ref !== 'string' || !ref.includes('outputFields.')) return null;
  const match = ref.match(/\$\{nodes\.([^}]+)\.outputFields\.([^}]+)\}/) || ref.match(/nodes\.([^.]+)\.outputFields\.([^.]+)/);
  if (!match) return null;
  return { nodeId: match[1].trim(), fieldKey: match[2].trim() };
}

/** Human-readable label for output field key (e.g. desc -> Description, name -> Name). */
function fieldKeyLabel(key) {
  if (key === 'desc') return 'Description';
  return (key && key.charAt(0).toUpperCase() + key.slice(1)) || key;
}

/** For body/formData fields, return [{ key, title, valueDisplay, valueRef? }] only for keys with a filled value (read-only display). Empty/removed fields are scrubbed from the list. */
function getBodySubFields(f) {
  if (!isBodyFormDataField(f)) return [];
  const props = f.schema?.properties || {};
  return Object.keys(f.value)
    .filter((key) => !isValueEmpty(f.value[key]))
    .map((key) => {
      const v = f.value[key];
      const valueRef = typeof v === 'string' ? parseRef(v) : null;
      return {
        key,
        title: props[key]?.title || props[key]?.description || key,
        valueDisplay: valueRef ? null : formatSingleValue(v, props[key]),
        valueRef: valueRef || undefined,
      };
    });
}

/** Filter out null/undefined/empty string from body array values so they don't show or persist. */
function sanitizeBodyArray(arr) {
  if (!Array.isArray(arr)) return arr;
  return arr.filter((x) => x != null && x !== '');
}

/** For override view: body keys, expanding perform-generated nested objects (e.g. Sheets columns). */
function getBodySubFieldsForOverride(f) {
  if (!(f?.in === 'formData' || f?.name === 'body') || !f?.schema?.properties) return [];
  const valueObj = typeof f.value === 'object' && f.value && !Array.isArray(f.value) ? f.value : {};
  const rows = [];

  const walk = (schemaProps, valueAt, path, groupTitle) => {
    const keys = new Set([
      ...Object.keys(schemaProps || {}),
      ...Object.keys(valueAt && typeof valueAt === 'object' && !Array.isArray(valueAt) ? valueAt : {}),
    ]);
    keys.forEach((key) => {
      if (key === '') return;
      const propSchema = schemaProps?.[key];
      const v = valueAt?.[key];
      const nestedProps = propSchema?.properties;
      const isNestedObject =
        propSchema?.type === 'object' &&
        nestedProps &&
        typeof nestedProps === 'object' &&
        Object.keys(nestedProps).filter((k) => k !== '').length > 0;

      if (isNestedObject) {
        walk(
          nestedProps,
          v && typeof v === 'object' && !Array.isArray(v) ? v : {},
          [...path, key],
          propSchema.title || propSchema.description || key
        );
        return;
      }

      const valueRef = typeof v === 'string' ? parseRef(v) : null;
      rows.push({
        key: [...path, key].join('.'),
        bodyPath: [...path, key],
        title: propSchema?.title || propSchema?.description || key,
        groupTitle: groupTitle || null,
        valueDisplay: valueRef ? null : formatSingleValue(v, propSchema),
        valueRef: valueRef || undefined,
        required: propSchema?.required === true,
      });
    });
  };

  walk(f.schema.properties, valueObj, [], null);
  return rows;
}

const ADVANCED_WARNING_TITLE = 'Switch to Advanced editing';
const ADVANCED_WARNING_MESSAGE =
  "You're about to open the full step editor. Use this for event selection, testing, and detailed field mapping. Continue?";

const ReadOnlyActionPanel = ({
  selectedNode,
  breakpoint,
  user,
  tenant,
  onPaneClick,
  sendNode,
  deleteNode,
  workflowID,
  sendLoggedOutToAuthorizer,
  inlineLayout = false,
  store: storeProp,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const node = useSelector(selectElementById(selectedNode?.id));
  const elements = useSelector(selectWorkflowElements) || [];
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [appPickerOpen, setAppPickerOpen] = useState(false);
  const [eventPickerOpen, setEventPickerOpen] = useState(false);
  const [actionsMap, setActionsMap] = useState({});
  const [eventsLoading, setEventsLoading] = useState(false);
  const storeFromContext = useStore();
  const store = storeProp ?? storeFromContext;
  const [wizardStep, setWizardStep] = useState('setup'); // 'setup' | 'configure' | 'test'
  const [editingRow, setEditingRow] = useState(null); // { fieldIndex, bodyKey?, bodyPath? }
  const [editingFields, setEditingFields] = useState(null); // when set, FieldsRender uses this so Add/Remove re-render immediately
  const saveWorkflowTimeoutRef = useRef(null);
  const openEventAfterAppRef = useRef(false);
  const testSectionRef = useRef(null);
  const prevLoadingPerformRef = useRef(false);
  const loadingPerform = useSelector(selectLoadingPerform);

  useEffect(() => () => { if (saveWorkflowTimeoutRef.current) clearTimeout(saveWorkflowTimeoutRef.current); }, []);

  useEffect(() => {
    setWizardStep(getDefaultWizardStep(node));
    setEditingRow(null);
    setEditingFields(null);
  }, [selectedNode?.id]);

  // After perform finishes fetching dynamic columns/fields, close the editor so new rows appear.
  useEffect(() => {
    if (prevLoadingPerformRef.current && !loadingPerform) {
      setEditingRow(null);
      setEditingFields(null);
    }
    prevLoadingPerformRef.current = !!loadingPerform;
  }, [loadingPerform]);

  const handleAdvancedClick = () => setAdvancedOpen(true);
  const handleAdvancedClose = () => setAdvancedOpen(false);
  const handleAdvancedConfirm = () => {
    setAdvancedOpen(false);
    // Defer switching to manual mode until after the Dialog has fully closed and unmounted.
    // This avoids the panel mounting while MUI Dialog's portal is still in the tree (which can lose Redux context).
    requestAnimationFrame(() => {
      setAdvancedMode(true);
    });
  };

  const d = node?.data || {};
  const fields = d.fields || [];
  const hasApp = !!(d.app || d.type);
  const hasEvent = !!(d.action && String(d.action).trim());
  const appLabel = d.label || d.app || d.type || 'App';
  const actionName = d.actionlabel || d.actionLabel || d.action || 'Select an event';
  const icon = d.icon;
  const appUrl = d.url || '';

  // Load action events when app URL is available
  useEffect(() => {
    if (!hasApp || !appUrl || advancedMode) {
      setActionsMap({});
      return undefined;
    }
    let cancelled = false;
    setEventsLoading(true);
    axios.request({ method: 'GET', url: appUrl })
      .then((res) => {
        if (!cancelled) setActionsMap(res.data?.actions || {});
      })
      .catch(() => {
        if (!cancelled) setActionsMap({});
      })
      .finally(() => {
        if (!cancelled) {
          setEventsLoading(false);
          if (openEventAfterAppRef.current) {
            openEventAfterAppRef.current = false;
            setEventPickerOpen(true);
          }
        }
      });
    return () => { cancelled = true; };
  }, [hasApp, appUrl, selectedNode?.id, advancedMode]);

  const eventOptions = useMemo(() => {
    return Object.entries(actionsMap)
      .filter(([, v]) => v && v.hidden !== true)
      .map(([key, v]) => ({
        key,
        title: v.title || key,
        description: v.description || '',
        badge: v.method === 'post' ? 'Create' : v.method === 'get' ? 'Search' : v.method === 'put' || v.method === 'patch' ? 'Update' : undefined,
        raw: v,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [actionsMap]);

  // Auto-open app picker when configuring a blank step
  useEffect(() => {
    if (!advancedMode && wizardStep === 'setup' && !hasApp) {
      setAppPickerOpen(true);
    }
  }, [selectedNode?.id, hasApp, advancedMode, wizardStep]);

  // Auto-open event picker when app is set but no event yet
  useEffect(() => {
    if (!advancedMode && wizardStep === 'setup' && hasApp && !hasEvent && !appPickerOpen && Object.keys(actionsMap).length > 0) {
      setEventPickerOpen(true);
    }
  }, [selectedNode?.id, hasApp, hasEvent, advancedMode, wizardStep, appPickerOpen, actionsMap]);

  const handleAppSelect = (appObj) => {
    if (!selectedNode?.id || !appObj) return;
    saveRecentApp(appObj, 'action');
    const label = appObj.name || appObj.app;
    dispatch(elementUpdated(
      selectedNode.id,
      {
        ...d,
        name: 'Add action',
        type: appObj.app,
        app: appObj.app,
        label,
        icon: appObj.icon || '',
        hasauth: appObj.auth === true,
        custombutton: appObj.custombutton,
        url: appObj.formDataEndpoint || appObj.url || '',
        action: '',
        actionlabel: '',
        actionKey: '',
        credential: null,
        authname: null,
        fields: [],
        testData: null,
        actionTestTime: null,
      },
      null,
      'changed app'
    ));
    dispatch(saveWorkflow(1));
    setWizardStep('setup');
    setEditingRow(null);
    setEditingFields(null);
    setActionsMap({});
    openEventAfterAppRef.current = true;
  };

  const handleEventSelect = (eventOpt) => {
    if (!selectedNode?.id || !eventOpt) return;
    const schema = actionsMap[eventOpt.key] || eventOpt.raw || {};
    const freshFields = Array.isArray(schema.fields)
      ? schema.fields.map((f) => {
          const next = { ...f };
          delete next.value;
          return next;
        })
      : [];
    const eventTitle = eventOpt.title || eventOpt.key;
    dispatch(elementUpdated(
      selectedNode.id,
      {
        ...d,
        action: eventOpt.key,
        actionlabel: eventTitle,
        actionKey: eventOpt.key,
        name: `${eventTitle} in ${appLabel}`,
        fields: freshFields,
        testData: null,
        actionTestTime: null,
      },
      null,
      'changed event'
    ));
    dispatch(saveWorkflow(1));
    // Stay on setup until auth is done; otherwise go to configure fields
    const needsAuth = d.hasauth === true || node?.data?.hasauth === true;
    const hasCred = !!(d.credential || d.authname);
    setWizardStep(needsAuth && !hasCred ? 'setup' : 'configure');
    setEditingRow(null);
    setEditingFields(null);
  };

  const renderAppPickerButton = () => (
    <Tooltip title="Change app">
      <IconButton
        className={classes.appIconButton}
        onClick={() => setAppPickerOpen(true)}
        aria-label="Choose app"
        size="small"
      >
        {icon ? (
          <img src={icon} alt={appLabel} className={classes.appIcon} />
        ) : (
          <div className={classes.appIconPlaceholder}>{(hasApp ? appLabel : 'A').charAt(0).toUpperCase()}</div>
        )}
      </IconButton>
    </Tooltip>
  );

  if (advancedMode) {
    return (
      <Provider store={store}>
        {/* ActionPanel uses useStoreState from react-flow-renderer, which uses a different Redux context
            than app react-redux. Wrapping in ReactFlowProvider provides that context so manual mode doesn't crash. */}
        <ReactFlowProvider>
          <ActionPanel
            key={selectedNode?.id}
            breakpoint={breakpoint}
            user={user}
            tenant={tenant}
            selectedNode={selectedNode}
            onPaneClick={onPaneClick}
            sendNode={sendNode}
            deleteNode={deleteNode}
            workflowID={workflowID}
            sendLoggedOutToAuthorizer={sendLoggedOutToAuthorizer}
            inlineLayout={inlineLayout}
          />
        </ReactFlowProvider>
      </Provider>
    );
  }

  const configStatus = getStepConfigStatus(node);
  const { setupDone, configureDone } = getWizardStepState(node);

  const handleStatusClick = () => {
    switch (configStatus.key) {
      case 'needs_app':
        setWizardStep('setup');
        setAppPickerOpen(true);
        break;
      case 'needs_event':
        setWizardStep('setup');
        setEventPickerOpen(true);
        break;
      case 'needs_auth':
        setWizardStep('setup');
        break;
      case 'needs_fields':
        setWizardStep('configure');
        break;
      case 'needs_test':
      case 'test_failed':
        setWizardStep('test');
        setTimeout(() => testSectionRef.current?.runTest?.(), 0);
        break;
      case 'ready':
      default:
        setWizardStep('test');
        break;
    }
  };

  const handleSetupContinue = () => {
    if (!hasApp) {
      setAppPickerOpen(true);
      return;
    }
    if (!hasEvent) {
      setEventPickerOpen(true);
      return;
    }
    if (stepRequiresAuth(node) && !hasAuth(node)) {
      return;
    }
    setWizardStep('configure');
  };

  const handleConfigureContinue = () => {
    if (!configureDone) return;
    setEditingRow(null);
    setEditingFields(null);
    setWizardStep('test');
  };

  // Resolve ref to source app icon + label for chips (read-only and override)
  const resolveRefChip = (valueRef) => {
    if (!valueRef || !elements.length) return null;
    const sourceEl = elements.find((el) => !el.source && !el.target && el.id === valueRef.nodeId);
    const iconUrl = sourceEl?.data?.icon;
    const label = fieldKeyLabel(valueRef.fieldKey);
    return (
      <Chip
        size="small"
        avatar={iconUrl ? <Avatar src={iconUrl} style={{ width: 20, height: 20 }} /> : undefined}
        label={label}
        className={classes.chip}
        variant="outlined"
      />
    );
  };

  const isBodyValueEmpty = (v) => {
    if (v == null || v === '') return true;
    if (Array.isArray(v) && v.length === 0) return true;
    if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return true;
    return false;
  };
  const scrubBodyInFields = (fieldsArr) => {
    if (!Array.isArray(fieldsArr)) return fieldsArr;
    const seen = new Set();
    return fieldsArr
      .filter((f) => {
        const key = `${f?.name ?? ''}:${f?.in ?? ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((f) => {
        if (f.name !== 'body' || f.schema?.type !== 'object' || !f.value || typeof f.value !== 'object' || Array.isArray(f.value)) return f;
        const scrubbed = Object.fromEntries(
          Object.entries(f.value)
            .map(([k, v]) => [k, Array.isArray(v) ? sanitizeBodyArray(v) : v])
            .filter(([, v]) => !isBodyValueEmpty(v) || (Array.isArray(v) && v.length === 0))
        );
        return { ...f, value: scrubbed };
      });
  };
  const handleSetFields = (newFields) => {
    if (!selectedNode?.id || !node?.data) return;
    const current = store?.getState?.()?.workflow?.present?.elements?.find((el) => el.id === selectedNode.id);
    const base = current?.data || node.data;
    const merged = mergeFieldsPreferRicherBody(current?.data?.fields || base.fields, newFields);
    const scrubbed = scrubBodyInFields(merged);
    dispatch(elementUpdated(selectedNode.id, { ...base, fields: scrubbed }, null));
    if (saveWorkflowTimeoutRef.current) clearTimeout(saveWorkflowTimeoutRef.current);
    saveWorkflowTimeoutRef.current = setTimeout(() => {
      saveWorkflowTimeoutRef.current = null;
      dispatch(saveWorkflow(1));
    }, 500);
  };
  const flushOverrideSave = () => {
    if (saveWorkflowTimeoutRef.current) {
      clearTimeout(saveWorkflowTimeoutRef.current);
      saveWorkflowTimeoutRef.current = null;
    }
    dispatch(saveWorkflow(1));
  };
  const sameBodyPath = (a, b) => JSON.stringify(a || null) === JSON.stringify(b || null);
  const overrideRows = [];
  (fields || []).forEach((f, idx) => {
    if (f.hidden) return;
    if (f.in === 'formData' || f.name === 'body') {
      const bodyRequired = Array.isArray(f.schema?.required) ? f.schema.required : [];
      getBodySubFieldsForOverride(f).forEach((s) => {
        const bodyPath = s.bodyPath || [s.key];
        const topKey = bodyPath[0];
        const prop = getSchemaAtPath(f.schema, bodyPath);
        overrideRows.push({
          type: 'body',
          fieldIndex: idx,
          bodyKey: topKey,
          bodyPath,
          title: s.title,
          groupTitle: s.groupTitle || null,
          valueDisplay: s.valueDisplay,
          valueRef: s.valueRef,
          required: bodyRequired.includes(topKey) || s.required === true || prop?.required === true,
        });
      });
    } else {
      const label = formatFieldDisplayLabel(f);
      const valueRef = typeof f?.value === 'string' ? parseRef(f.value) : null;
      overrideRows.push({
        type: 'path',
        fieldIndex: idx,
        bodyKey: null,
        bodyPath: null,
        title: f.title || f.name || 'Field',
        groupTitle: null,
        valueDisplay: valueRef ? null : label,
        valueRef: valueRef || undefined,
        required: f.required === true || f.schema?.required === true,
      });
    }
  });

  const panelWrapStyle = inlineLayout ? { position: 'relative', width: '100%', height: '100%', minHeight: 0, paddingTop: '75px', paddingLeft: 6, paddingRight: 12, overflow: 'auto' } : {};

  const renderAccountSection = () => (
    (stepRequiresAuth(node) || hasAuth(node)) && (
      <Box id={`step-auth-${selectedNode?.id}`} style={{ marginBottom: 12 }}>
        <Typography className={classes.sectionLabel} style={{ marginTop: 12 }}>
          Account
        </Typography>
        {!hasAuth(node) && (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            Connect or select an account to run this step.
          </Typography>
        )}
        <Authorizer
          user={user}
          sendLoggedOutToAuthorizer={sendLoggedOutToAuthorizer}
          tenant={tenant}
          node={node}
          app={d.app || d.type}
          applabel={appLabel}
          icon={icon}
          sendData={(credentialId) => {
            const current = store?.getState?.()?.workflow?.present?.elements?.find((el) => el.id === selectedNode.id);
            const base = current?.data || d;
            dispatch(elementUpdated(selectedNode.id, { ...base, credential: credentialId }, null));
            dispatch(saveWorkflow(1));
          }}
          sendAuth={(authname) => {
            const current = store?.getState?.()?.workflow?.present?.elements?.find((el) => el.id === selectedNode.id);
            const base = current?.data || d;
            dispatch(elementUpdated(selectedNode.id, { ...base, authname }, null));
            dispatch(saveWorkflow(1));
          }}
          saveNode={() => dispatch(saveWorkflow(1))}
          value={d.authname}
          breakpoint={breakpoint}
        />
        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
          Use Change to switch accounts, reconnect, or add a new {appLabel} connection.
        </Typography>
      </Box>
    )
  );

  const renderConfigureFields = () => (
    <>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
        Set the values this step needs. Required fields must be filled before testing.
      </Typography>
      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 4, gap: 8 }}>
        <Typography className={classes.sectionLabel} style={{ marginBottom: 0 }}>Fields</Typography>
        {canRefreshPerformFields(node) ? (
          <Button
            size="small"
            color="primary"
            startIcon={loadingPerform ? <CircularProgress size={14} /> : <RefreshRoundedIcon style={{ fontSize: 16 }} />}
            disabled={loadingPerform}
            onClick={async () => {
              try {
                await refreshPerformFields(node, {
                  dispatch,
                  loadingperform,
                  elementUpdated,
                  saveWorkflow,
                });
              } catch (err) {
                // Keep UI quiet; button stays available to retry
                console.error('Refresh fields failed', err);
              }
            }}
            style={{ textTransform: 'none', flexShrink: 0 }}
          >
            Refresh fields
          </Button>
        ) : null}
      </Box>
      {loadingPerform ? (
        <Box display="flex" alignItems="center" style={{ gap: 10, marginBottom: 12 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="textSecondary">
            Loading fields from your account…
          </Typography>
        </Box>
      ) : null}
      <Box className={classes.overrideScroll}>
        {overrideRows.length === 0 ? (
          <Typography variant="body2" color="textSecondary">No fields for this event.</Typography>
        ) : (
          overrideRows.map((row, rIdx) => {
            const isEditingThisRow =
              editingRow?.fieldIndex === row.fieldIndex &&
              sameBodyPath(editingRow?.bodyPath, row.bodyPath) &&
              editingRow?.bodyKey === row.bodyKey;
            const rowField = getOverrideRowField(fields, row);
            const unmetDeps = getUnmetDynamicParams(rowField, fields);
            const depsBlocked = unmetDeps.length > 0;
            const depsHint = depsBlocked
              ? `Select ${unmetDeps.map((dep) => dep.label).join(', ')} first`
              : '';
            const prevGroup = rIdx > 0 ? overrideRows[rIdx - 1].groupTitle : null;
            const showGroup = row.groupTitle && row.groupTitle !== prevGroup;
            const bodyPath = row.bodyPath;
            const isNestedLeaf = Array.isArray(bodyPath) && bodyPath.length > 1;
            return (
              <React.Fragment key={`${row.fieldIndex}-${row.bodyPath ? row.bodyPath.join('.') : row.bodyKey ?? 'path'}-${rIdx}`}>
                {showGroup ? (
                  <Typography className={classes.sectionLabel} style={{ marginTop: rIdx === 0 ? 0 : 16 }}>
                    {row.groupTitle}
                  </Typography>
                ) : null}
                <Box style={{ marginBottom: 8, opacity: depsBlocked ? 0.65 : 1 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {row.title}
                      {row.required ? <span style={{ color: '#d32f2f', marginLeft: 2 }}>*</span> : null}
                    </Typography>
                    <Box display="flex" alignItems="center" flexWrap="wrap" style={{ marginTop: 2 }}>
                      {depsBlocked ? (
                        <Typography variant="body2" color="textSecondary">{depsHint}</Typography>
                      ) : row.valueRef ? resolveRefChip(row.valueRef) : (
                        <Typography variant="body2" color="textSecondary" style={{ whiteSpace: 'pre-line' }}>
                          {row.valueDisplay != null ? row.valueDisplay : '—'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Tooltip title={depsBlocked ? depsHint : ''} placement="top">
                    <span>
                      <Button
                        size="small"
                        color="primary"
                        disabled={depsBlocked || loadingPerform}
                        onClick={() => {
                          if (depsBlocked) return;
                          setEditingRow(
                            isEditingThisRow
                              ? null
                              : { fieldIndex: row.fieldIndex, bodyKey: row.bodyKey, bodyPath: row.bodyPath }
                          );
                          setEditingFields(null);
                        }}
                      >
                        Change &gt;
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                {isEditingThisRow && !depsBlocked && (
                  <Box style={{ marginTop: 12, paddingTop: 12, paddingLeft: 8, borderLeft: '2px solid #0069ea', marginLeft: 4 }}>
                    {row.bodyKey != null && !isNestedLeaf ? (
                      <>
                        <FieldsRender
                          breakpoint={breakpoint}
                          fields={(() => {
                            const full = (editingFields != null ? editingFields : fields) || [];
                            const bodyIdx = full.findIndex((f) => f.name === 'body' && (f.in === 'formData' || !f.in));
                            if (bodyIdx < 0 || !full[bodyIdx]?.schema?.properties?.[row.bodyKey]) return full;
                            const bodyField = full[bodyIdx];
                            const propSchema = bodyField.schema.properties[row.bodyKey];
                            const value = bodyField.value && typeof bodyField.value === 'object' && !Array.isArray(bodyField.value)
                              ? { ...bodyField.value }
                              : {};
                            if (propSchema?.type === 'array' && !Array.isArray(value[row.bodyKey])) value[row.bodyKey] = [];
                            return full.map((f, idx) => idx === bodyIdx ? { ...f, value } : f);
                          })()}
                          bodyKeyOnly={row.bodyKey}
                          setFields={(updated) => {
                            const current = editingFields != null ? editingFields : (fields || []);
                            const next = typeof updated === 'function' ? updated(current) : (Array.isArray(updated) ? updated : current);
                            handleSetFields(next);
                            setEditingFields(next);
                          }}
                          disabled={false}
                        />
                        <Button size="small" variant="contained" color="primary" onClick={() => { flushOverrideSave(); setEditingRow(null); setEditingFields(null); }} style={{ marginTop: 8, marginLeft: 12 }}>
                          Done
                        </Button>
                      </>
                    ) : isNestedLeaf ? (
                      <SingleFieldEditor
                        fields={(() => {
                          const bodyField = fields[row.fieldIndex];
                          const leafSchema = getSchemaAtPath(bodyField?.schema, bodyPath) || { type: 'string' };
                          const leafValue = getNestedValue(bodyField?.value, bodyPath);
                          return [{
                            name: bodyPath[bodyPath.length - 1],
                            title: row.title,
                            schema: leafSchema,
                            value: leafValue ?? '',
                            required: row.required,
                          }];
                        })()}
                        fieldIndex={0}
                        bodyKey={null}
                        setFields={(updated) => {
                          const nextLeaf = Array.isArray(updated) ? updated[0]?.value : undefined;
                          const current = store?.getState?.()?.workflow?.present?.elements?.find((el) => el.id === selectedNode.id);
                          const latestFields = current?.data?.fields || fields || [];
                          const next = latestFields.map((f, i) => {
                            if (i !== row.fieldIndex) return f;
                            const baseVal = f.value && typeof f.value === 'object' && !Array.isArray(f.value) ? f.value : {};
                            return { ...f, value: setNestedValue(baseVal, bodyPath, nextLeaf) };
                          });
                          handleSetFields(next);
                        }}
                        onClose={() => { flushOverrideSave(); setEditingRow(null); setEditingFields(null); }}
                        breakpoint={breakpoint}
                      />
                    ) : (
                      <SingleFieldEditor
                        fields={editingFields != null ? editingFields : fields}
                        setFields={handleSetFields}
                        fieldIndex={row.fieldIndex}
                        bodyKey={null}
                        onClose={() => { flushOverrideSave(); setEditingRow(null); setEditingFields(null); }}
                        breakpoint={breakpoint}
                      />
                    )}
                  </Box>
                )}
              </Box>
              </React.Fragment>
            );
          })
        )}
      </Box>
      <Button
        className={classes.footerContinue}
        variant="contained"
        color="primary"
        disabled={!configureDone || loadingPerform}
        onClick={handleConfigureContinue}
        fullWidth
      >
        {loadingPerform ? 'Loading fields…' : configureDone ? 'Continue' : 'To continue, finish required fields'}
      </Button>
    </>
  );

  return (
    <div className={inlineLayout ? undefined : styles.Panel} style={{ paddingTop: '75px', ...panelWrapStyle }}>
    <Box className={classes.panelWrapper}>
      <Box className={classes.headerBar}>
        <div className={classes.headerLogoCircle}>
          <img src="/new-logo-only-512-512.png" alt="Workload" />
        </div>
        <Typography className={classes.headerTitle}>How this step works</Typography>
        <Box style={{ marginLeft: 'auto' }}>
          <IconButton size="small" onClick={onPaneClick} aria-label="Close" style={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <Box className={inlineLayout ? `${classes.contentArea} ${classes.contentAreaInline}` : classes.contentArea}>
      <StepProgressPills
        node={node}
        activeStep={wizardStep}
        onStepChange={(step) => {
          setEditingRow(null);
          setEditingFields(null);
          setWizardStep(step);
        }}
      />
      <Box
        role="button"
        tabIndex={0}
        onClick={handleStatusClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleStatusClick();
          }
        }}
        className={`${classes.statusBanner} ${
          configStatus.tone === 'ready' ? classes.statusBannerReady : classes.statusBannerNeeds
        }`}
      >
        <Button
          className={`${classes.statusBannerButton} ${
            configStatus.tone === 'ready' ? classes.statusReady : classes.statusNeedsInput
          }`}
          disableRipple
          onClick={(e) => {
            e.stopPropagation();
            handleStatusClick();
          }}
        >
          {configStatus.tone === 'ready' ? '✓' : '→'} {configStatus.label}
        </Button>
      </Box>
      <Divider className={classes.divider} />

      {wizardStep === 'setup' && (
        <>
          <Typography className={classes.sectionLabel}>App</Typography>
          <Box className={classes.header}>
            {renderAppPickerButton()}
            <Box>
              <Typography variant="body1" className={classes.actionName}>{hasApp ? appLabel : 'Select an app'}</Typography>
              <Typography variant="caption" color="textSecondary">
                {hasApp ? 'Click the icon to change the app' : 'Click the icon to choose an app'}
              </Typography>
            </Box>
          </Box>

          {hasApp && (
            <>
              <Typography className={classes.sectionLabel}>Action event</Typography>
              <Box className={classes.eventRow}>
                <Box flex={1} minWidth={0}>
                  <Typography variant="body1" style={{ fontWeight: 600 }}>
                    {hasEvent ? actionName : 'Select an event'}
                  </Typography>
                  {!hasEvent && (
                    <Typography variant="caption" color="textSecondary">Choose what this step should do</Typography>
                  )}
                </Box>
                <Button size="small" color="primary" onClick={() => setEventPickerOpen(true)}>
                  {hasEvent ? 'Change' : 'Choose'}
                </Button>
              </Box>
              {renderAccountSection()}
            </>
          )}

          <Button
            className={classes.footerContinue}
            variant="contained"
            color="primary"
            disabled={!setupDone}
            onClick={handleSetupContinue}
            fullWidth
          >
            {!hasApp
              ? 'Choose an app to continue'
              : !hasEvent
                ? 'Choose an event to continue'
                : stepRequiresAuth(node) && !hasAuth(node)
                  ? 'Connect an account to continue'
                  : 'Continue'}
          </Button>
        </>
      )}

      {wizardStep === 'configure' && renderConfigureFields()}

      {wizardStep === 'test' && (
        <StepTestSection
          ref={testSectionRef}
          mode="action"
          node={node}
          nodeId={selectedNode?.id}
          onEditFields={() => setWizardStep('configure')}
          onContinue={onPaneClick}
        />
      )}

      <Box className={classes.buttonRow}>
        <Button
          variant="text"
          size="small"
          onClick={handleAdvancedClick}
          style={{ color: 'gray' }}
        >
          Advanced editing
        </Button>
      </Box>

      <Dialog open={advancedOpen} onClose={handleAdvancedClose}>
        <DialogTitle>{ADVANCED_WARNING_TITLE}</DialogTitle>
        <DialogContent>
          <DialogContentText>{ADVANCED_WARNING_MESSAGE}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAdvancedClose}>Cancel</Button>
          <Button onClick={handleAdvancedConfirm} color="primary" variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <AppPickerModal
        open={appPickerOpen}
        onClose={() => setAppPickerOpen(false)}
        onSelect={handleAppSelect}
        mode="action"
      />
      <EventPickerModal
        open={eventPickerOpen}
        onClose={() => setEventPickerOpen(false)}
        onSelect={handleEventSelect}
        events={eventOptions}
        loading={eventsLoading}
        selectedKey={d.action || ''}
        title="Choose an action event"
        searchPlaceholder="Search actions…"
      />
      </Box>
    </Box>
    </div>
  );
};

export default ReadOnlyActionPanel;
